import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { twMerge } from 'tailwind-merge';
import { faPlus } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faMusic } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import ProgressCircle from '@components/ProgressCircle';
import { Button } from '@components/main-components/button/Button';
import { FeedPostModalMainSound } from '@components/modals/FeedPostModal/FeedPostModalMainSound';
import { useModal } from '@context/modal';
import type { FeedPostModalMainSoundFragment } from '@graphql/reactQuery';

const MAX_CHARACTERS = 280;

export function FeedPostModalMain({
  onChangeSoundClicked,
  release,
  postText,
  onPostTextChange,
  onSubmitClicked,
  isMutating,
  allowEdit,
}: {
  onChangeSoundClicked: () => void;
  postText: string;
  allowEdit: boolean;
  onPostTextChange: (text: string) => void;
  release: FeedPostModalMainSoundFragment | undefined;
  onSubmitClicked: () => void;
  isMutating: boolean;
}) {
  const { closeModal } = useModal();
  const [postProgressPercent, setPostProgressPercent] = useState(0);
  const isTextTooLong = postText.length > MAX_CHARACTERS;

  const submitEnabled = postText.length > 0 && release && !isTextTooLong;

  return (
    <>
      {/**
       * Text area and progress circle
       */}
      <div
        className={`relative w-full flex-1 rounded-md ${
          isTextTooLong ? `bg-destructive50` : `bg-neutral100`
        } `}
      >
        <textarea
          value={postText}
          onChange={e => {
            const text = e.target.value;

            onPostTextChange(text);
            setPostProgressPercent((text.length / MAX_CHARACTERS) * 100);
          }}
          className={twMerge(
            `scrollbar-light h-full w-full resize-none rounded-md bg-transparent`,
            `px-3 py-2 ring-inset ring-base300`,
            `hover:bg-neutral50 hover:ring-1`,
            `focus:!bg-neutral100 focus:outline-none focus:!ring-2 focus:ring-black`,
            isTextTooLong ? `text-destructive400` : `text-base900`,
          )}
          placeholder="Write a Post..."
        />
        <div
          className={`absolute bottom-2 right-2 flex items-center gap-1 ${
            isTextTooLong ? `bg-destructive50` : `bg-neutral100`
          }`}
        >
          <div
            className={`text-base-s ${isTextTooLong ? `text-destructive400` : `text-neutral400`}`}
          >
            {postText.length}/{MAX_CHARACTERS}
          </div>
          <ProgressCircle
            percent={Math.min(postProgressPercent, 100)}
            size={16}
            remainingColor="#E6E6E6"
            usedColor={isTextTooLong ? '#D22F2F' : '#0e1213'}
          />
        </div>
      </div>

      {/**
       * Select sound button
       */}
      {release ? (
        <FeedPostModalMainSound
          allowEdit={allowEdit}
          release={release}
          onEditSoundClicked={onChangeSoundClicked}
        />
      ) : (
        <button
          onClick={onChangeSoundClicked}
          className="flex cursor-pointer items-center gap-4 rounded-md border border-base300 p-3 hover:bg-neutral50"
        >
          <div className="flex h-16 w-16 items-center justify-center rounded-md bg-black">
            <FontAwesomeIcon icon={faMusic} className="text-white" />
          </div>
          <div className="flex-1 text-left font-title text-title-xxs font-medium text-neutral700">
            Select sound
          </div>
          <FontAwesomeIcon icon={faPlus} size="lg" className="text-base500" />
        </button>
      )}

      {/**
       * Bottom button row
       */}
      <div className="flex gap-3">
        <Button
          fullWidth
          onClick={() => {
            // if they have post text or a sound selected, show a confirmation modal
            if (
              postText.length &&
              confirm('Are you sure? You changes will not be saved.') === false
            ) {
              return;
            }

            closeModal();
          }}
          label="Cancel"
          className="flex-1"
          variant="secondary"
        />
        <Button
          fullWidth
          disabled={!submitEnabled}
          label="Post"
          className="flex-1"
          loading={isMutating}
          variant="primary"
          onClick={onSubmitClicked}
          type="submit"
        />
      </div>
    </>
  );
}
